/**
 * Technician view, today's list (tracker item 42, phase 2): the signed-in
 * user's jobs for a day (Today / Tomorrow), the unfinished group, and the
 * running-timer banner. Office roles can pick a technician. No prices.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, ITechnicianApi, IUsersApi } from '../api';
import * as types from '../api/types';
import { JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';
import { useAuth } from '../context/AuthContext';
import { useNow } from '../components/useNow';
import { inputClass, labelClass, secondaryButtonClass } from '../components/formStyles';
import { getErrorMessage } from '../utils/apiError';
import { addDays, isDateOnly } from '../utils/recurrence';
import { canViewOtherTechnicianDay } from '../utils/permissions';
import { checklistProgressLabel, elapsedSeconds, formatAddress, formatElapsed, timeWindowLabel } from '../utils/technician';
import { TechnicianStatusBadge } from './technicianUi';

type TechApi = ITechnicianApi & Pick<IUsersApi, 'listAssignableUsers'>;

function formatDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function JobCard({ card, userId, showDate }: { card: types.TechnicianJobCard; userId: string; showDate?: boolean }) {
  const progress = checklistProgressLabel(card.checklist);
  return (
    <li>
      <Link
        to={`/tech/jobs/${card.id}`}
        className="block min-h-[44px] rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-sm font-semibold text-gray-900">
            {showDate && card.scheduledDate ? `${formatDay(card.scheduledDate)} - ` : ''}
            {timeWindowLabel(card)}
          </span>
          <TechnicianStatusBadge card={card} userId={userId} />
        </div>
        <div className="mt-1 text-base font-semibold text-gray-900">{card.customer.name}</div>
        <div className="text-sm text-gray-600">{formatAddress(card.property)}</div>
        <div className="mt-1"><JobEnrollmentChip job={card} linked={false} /></div>
        <div className="mt-2 flex flex-wrap gap-x-4 text-xs text-gray-500">
          {card.route && <span>Route: {card.route.name}</span>}
          {progress && <span>Checklist {progress}</span>}
        </div>
      </Link>
    </li>
  );
}

function RunningBanner({ entry }: { entry: types.TechnicianTimeEntry }) {
  const now = useNow(true);
  return (
    <Link
      to={`/tech/jobs/${entry.jobId}`}
      className="mb-4 flex min-h-[44px] items-center justify-between gap-3 rounded-lg bg-yellow-100 p-4 text-yellow-900 ring-1 ring-yellow-300"
    >
      <span className="text-sm font-semibold">Timer running - open job</span>
      <span className="font-mono text-lg font-bold" data-testid="running-elapsed">{formatElapsed(elapsedSeconds(entry.startedAt, now))}</span>
    </Link>
  );
}

export function TechnicianTodayPage() {
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient() as TechApi, []);
  const [params, setParams] = useSearchParams();
  const dateParam = params.get('date') ?? '';
  const userIdParam = params.get('userId') ?? '';
  const date = isDateOnly(dateParam) ? dateParam : '';
  const canPick = canViewOtherTechnicianDay(user);

  const [data, setData] = useState<types.TechnicianToday | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [technicians, setTechnicians] = useState<types.AssignableUser[]>([]);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getTechnicianToday({ date: date || undefined, userId: canPick && userIdParam ? userIdParam : undefined });
      if (id !== requestId.current) return;
      setData(result);
      setToday(result.today);
    } catch (err) {
      if (id !== requestId.current) return;
      setData(null);
      setError(getErrorMessage(err, 'Could not load your jobs.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [api, date, userIdParam, canPick]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canPick) return;
    let cancelled = false;
    api.listAssignableUsers().then(
      (users) => { if (!cancelled) setTechnicians(users.filter((u) => u.role === types.UserRole.TECHNICIAN)); },
      () => { /* the picker is optional */ },
    );
    return () => { cancelled = true; };
  }, [api, canPick]);

  const updateParams = (next: { date?: string | null; userId?: string | null }) => {
    const p = new URLSearchParams(params);
    if (next.date !== undefined) { if (next.date) p.set('date', next.date); else p.delete('date'); }
    if (next.userId !== undefined) { if (next.userId) p.set('userId', next.userId); else p.delete('userId'); }
    setParams(p, { replace: true });
  };

  const tomorrow = today ? addDays(today, 1) : null;
  const effectiveDate = data?.date ?? (date || today);
  const isToday = !date || (today !== null && date === today);
  const isTomorrow = tomorrow !== null && date === tomorrow;
  const toggleClass = (active: boolean) =>
    `flex-1 min-h-[44px] rounded-md px-3 text-sm font-semibold ${active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'} disabled:opacity-50`;

  const viewerId = data?.user.id ?? user?.id ?? '';
  const viewingOther = !!data && !!user && data.user.id !== user.id;

  return (
    <div>
      <div className="mb-4 flex gap-2" role="group" aria-label="Day">
        <button type="button" className={toggleClass(isToday)} aria-pressed={isToday} onClick={() => updateParams({ date: null })}>
          Today
        </button>
        <button
          type="button"
          className={toggleClass(isTomorrow)}
          aria-pressed={isTomorrow}
          disabled={!tomorrow}
          onClick={() => tomorrow && updateParams({ date: tomorrow })}
        >
          Tomorrow
        </button>
      </div>

      {canPick && (
        <div className="mb-4">
          <label htmlFor="tech-picker" className={labelClass}>Technician</label>
          <select id="tech-picker" className={inputClass + ' min-h-[44px]'} value={userIdParam} onChange={(e) => updateParams({ userId: e.target.value })}>
            <option value="">Me</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>{t.email}</option>
            ))}
          </select>
        </div>
      )}

      {effectiveDate && (
        <h1 className="mb-3 text-lg font-bold text-gray-900">
          {formatDay(effectiveDate)}
          {viewingOther && data && <span className="block text-sm font-normal text-gray-500">{data.user.email}</span>}
        </h1>
      )}

      {loading && !data && (
        <div className="flex justify-center py-10" role="status" aria-label="Loading jobs">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" />
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200">
          <p>{error}</p>
          <button type="button" className={secondaryButtonClass + ' mt-3'} onClick={() => void load()}>Try again</button>
        </div>
      )}

      {data && (
        <>
          {data.runningEntry && !viewingOther && <RunningBanner entry={data.runningEntry} />}

          {data.jobs.length === 0 ? (
            <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 ring-1 ring-gray-200">
              {isToday ? 'No jobs scheduled for today.' : 'No jobs scheduled for this day.'}
            </p>
          ) : (
            <ul className="space-y-3" aria-label="Jobs">
              {data.jobs.map((card) => <JobCard key={card.id} card={card} userId={viewerId} />)}
            </ul>
          )}

          {data.unfinished.length > 0 && (
            <section className="mt-8" aria-labelledby="unfinished-heading">
              <h2 id="unfinished-heading" className="mb-2 text-base font-bold text-gray-900">Unfinished</h2>
              <p className="mb-3 text-xs text-gray-500">Open jobs from earlier days.</p>
              <ul className="space-y-3" aria-label="Unfinished jobs">
                {data.unfinished.map((card) => <JobCard key={card.id} card={card} userId={viewerId} showDate />)}
              </ul>
              {data.unfinishedTruncated && (
                <p className="mt-3 text-xs text-gray-600" role="note">
                  Showing the oldest {data.unfinished.length} unfinished jobs. More are open; ask the office to clear old ones.
                </p>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
